/* eslint-disable @typescript-eslint/no-empty-function */
import { Injectable } from '@angular/core';
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { AnalysesService } from 'src/app/analyses/services/analyses.service';
import {
  AnalysesTypes,
  AnalysesTypesLowercase,
} from 'src/app/analyses/interfaces/analyses.types';
import { retry } from 'rxjs';
import { MeasurementAddModalComponent } from 'src/app/body-measurements/components/measurement-add-modal/measurement-add-modal.component';
import { BloodAddMarkerModalComponent } from 'src/app/blood/components/blood-add-marker-modal/blood-add-marker-modal.component';
import { AddModalComponent } from 'src/app/questionnaire/components/add-modal/add-modal.component';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

const modalConfig: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  windowClass: 'basic-modal',
  backdropClass: 'basic-modal-backdrop',
};

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(
    private readonly modals: NgbModal,
    private readonly router: Router,
    private readonly analysesService: AnalysesService
  ) {}

  openFullScreen(component: any) {
    return this.modals.open(component, {
      keyboard: false,
      windowClass: 'fullscreen-modal',
    });
  }

  open(
    id: string,
    type: AnalysesTypesLowercase | AnalysesTypes,
    redirect: string
  ): Promise<any> {
    if (type === AnalysesTypes.Questionnaire) {
      return this.router.navigate(['/healthscore/questionnaire'], {
        queryParams: { redirectUrl: redirect },
      });
    } else {
      return this.add(id, type as AnalysesTypesLowercase);
    }
  }

  add(analysisId: string, type: AnalysesTypesLowercase): Promise<any> {
    let ref: NgbModalRef;

    if (type === AnalysesTypesLowercase.Blood) {
      ref = this.openModal(analysisId, BloodAddMarkerModalComponent);
    } else if (type === AnalysesTypesLowercase.Measurements) {
      ref = this.openModal(analysisId, MeasurementAddModalComponent);
    } else if (type === AnalysesTypesLowercase.Habits) {
      ref = this.openModal(analysisId, AddModalComponent);
    } else {
      Sentry.captureMessage(
        'Trying to create unsupported modal, of type ' + type
      );
    }

    return ref.result
      .then((requestId) => {
        switch (type) {
          case AnalysesTypesLowercase.Blood: {
            const calculationId = requestId;
            this.router.navigate([
              '/',
              type,
              'analyses',
              analysisId,
              calculationId,
            ]);
            break;
          }
          default: {
            this.analysesService
              .getCalculationId(analysisId, requestId, type)
              .pipe(retry(5))
              .subscribe(({ result: { calculationId } }) => {
                this.router.navigate([
                  '/',
                  type,
                  'analyses',
                  analysisId,
                  calculationId,
                ]);
              });
          }
        }
      })
      .catch(() => {});
  }

  private openModal(analysisId: string, modal): NgbModalRef {
    const ref = this.modals.open(modal, modalConfig);
    ref.componentInstance.analysisId = analysisId;
    ref.componentInstance.id = analysisId; // Habits has ID instead of analysisId
    ref.componentInstance.editMode = false;
    return ref;
  }
}
